import type { Country } from "../Country.js";
import { Currency } from "../Currency.js";
import type { EconomicSubjectVatType } from "../EconomicSubjectVatType.js";
import type { ExchangeRate } from "../ExchangeRatesSetting.js";
import type { SubcategoryForCustomer } from "../Subcategory.js";
import type { SubscriptionParamsForPricing } from "../Subscription.js";
import type { SubscriptionDeliveryCount } from "../SubscriptionDeliveryCount.js";
import type { TaxedPrice } from "../TaxedPrice.js";
import { getGiftSubscriptionPrice } from "../giftSubscriptionPriceTable.js";
import { calculatePriceWithTax } from "./calculatePriceWithTax.js";
import { calculatePriceWithoutTax } from "./calculatePriceWithoutTax.js";
import { convertToCurrency } from "./convertToCurrency.js";
import { findSubcategoryOfSubscriptionVariant } from "./findSubcategoryOfSubscriptionVariant.js";
import { shouldApplyTax } from "./shouldApplyTax.js";

interface CalculatePriceOfGiftSubscriptionInput {
	subcategories: SubcategoryForCustomer[];
	destinationCountry: Country | "outsideEu";
	subscription: SubscriptionParamsForPricing;
	currency: Currency;
	vatType: EconomicSubjectVatType;
	exchangeRates: ExchangeRate[];
	deliveryCount: SubscriptionDeliveryCount;
}

export function calculatePriceOfGiftSubscription({
	subcategories,
	destinationCountry,
	subscription,
	currency,
	vatType,
	exchangeRates,
	deliveryCount,
}: CalculatePriceOfGiftSubscriptionInput): TaxedPrice {
	const subcategory = findSubcategoryOfSubscriptionVariant(subcategories, subscription.variant, true);

	const taxRate = shouldApplyTax(destinationCountry, vatType)
		? subcategory.vatRates.find(({ country }) => country === destinationCountry)?.rate
		: 0;

	if (taxRate === undefined) {
		throw new Error(`Tax rate not found for country ${destinationCountry}`);
	}

	const priceWithoutTaxCzk = getGiftSubscriptionPrice(subscription.selection, subscription.size, deliveryCount);

	let withoutTax = convertToCurrency(priceWithoutTaxCzk, Currency.CZK, currency, exchangeRates);
	let withTax = calculatePriceWithTax(withoutTax, taxRate);

	if (currency === Currency.CZK) {
		withTax = Math.ceil(withTax);
		withoutTax = calculatePriceWithoutTax(withTax, taxRate);
	}

	return {
		taxRate,
		withTax,
		withoutTax,
	};
}
